import { useEnabledFeatures } from './enabled-features/use-enabled-features';
import { modals, Text } from '@cad-arc';
import { useCallback } from 'react';
import { useApplicationType } from './use-application-type';
import { UnitStatusUpdateResponse } from '@mark43/cad-api';
import { useUpdateDispositionModal } from '../../dispositions/hooks/use-update-disposition-modal';

type UseReleaseUnitBaseParams = {
    isOnlyUnitAssignedToEvent: boolean;
    assignedEventNumber?: string;
    assignedEventLocation?: string;
    callSign?: string;
};
type UseReleaseUnitWithDispositionParams = UseReleaseUnitBaseParams & {
    eventId: number;
    unitId: number;
    isPrimary: boolean;
};
export function useReleaseUnit(
    params: UseReleaseUnitBaseParams | UseReleaseUnitWithDispositionParams
) {
    let eventId: number | undefined;
    let unitId: number | undefined;
    let isPrimary: boolean | undefined;
    if ('eventId' in params) {
        ({ eventId, unitId, isPrimary } = params);
    }
    const {
        isOnlyUnitAssignedToEvent,
        assignedEventNumber,
        assignedEventLocation,
        callSign,
    } = params;
    const {
        dispatchConfirmUnassigningEvents,
        firstResponderConfirmUnassigningEvents,
        unitLevelDispositionsEnabled,
        additionalDispositionsEnabled,
    } = useEnabledFeatures();

    const { isFirstResponder } = useApplicationType();

    const { openEventPanelAndShowUpdateDispositionModal } = useUpdateDispositionModal({
        eventId,
        unitId,
        isPrimaryUnit: isPrimary,
    });

    const confirmUnassigningEvents =
        (isFirstResponder && firstResponderConfirmUnassigningEvents) ||
        (!isFirstResponder && dispatchConfirmUnassigningEvents);

    const shouldShowUpdateDispositionModal =
        unitLevelDispositionsEnabled &&
        (isPrimary || (additionalDispositionsEnabled && !isPrimary));

    return useCallback(
        async ({
            callback,
        }: {
            callback: (...args: unknown[]) => Promise<UnitStatusUpdateResponse>;
        }) => {
            if (shouldShowUpdateDispositionModal) {
                return openEventPanelAndShowUpdateDispositionModal();
            }
            if (
                confirmUnassigningEvents &&
                isOnlyUnitAssignedToEvent &&
                !(await modals.confirm({
                    title: '',
                    body: (
                        <>
                            <Text>
                                {isFirstResponder
                                    ? 'Set status to Available'
                                    : `Release ${callSign}`}
                                ?
                            </Text>
                            <br />
                            <Text>
                                Event {assignedEventNumber} ({assignedEventLocation}) will become
                                unassigned.
                            </Text>
                        </>
                    ),
                }))
            ) {
                return undefined;
            }

            return await callback();
        },
        [
            confirmUnassigningEvents,
            isOnlyUnitAssignedToEvent,
            isFirstResponder,
            callSign,
            assignedEventNumber,
            assignedEventLocation,
            shouldShowUpdateDispositionModal,
            openEventPanelAndShowUpdateDispositionModal,
        ]
    );
}
